import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  OptionKey,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { EditableBadge, WarningMessage } from '@/modules/UseMyMoney/components';
import { AccountOrder } from '@/modules/UseMyMoney/queries';
import { useMyMoneyFormStore } from '@/modules/UseMyMoney/stores';
import { GroupServicesKey } from '@/modules/UseMyMoney/UseMyMoney.types';
import { useServiceOptions } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks';

import FieldSetupLabel from '../FieldSetupLabel/FieldSetupLabel';
import FieldSetupOptions from '../FieldSetupOptions/FieldSetupOptions';

import SelectADateOfServiceFirstWarning from './SelectADateOfServiceFirstWarning';
import ServiceDescriptionField from './ServiceDescriptionField';

interface Props {
  aiSuggestedAccountOrdersArrays?: AccountOrder[][];
  isAiSuggestedAccountOrdersLoading?: boolean;
  hasAvailableAccounts?: boolean;
}

const ServiceDescriptionFieldSetup: React.FC<Props> = ({
  aiSuggestedAccountOrdersArrays,
  isAiSuggestedAccountOrdersLoading,
  hasAvailableAccounts,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { values, handleSetFormValues } = useMyMoneyFormStore();
  const [servicesGrouping, setServicesGrouping] = useState(GroupServicesKey.CATEGORY);
  const {
    suggestedServiceOptions,
    activeServiceOption,
    serviceOptions,
  } = useServiceOptions({
    serviceId: values?.customServiceId,
    servicesGrouping,
  });
  const [isEditMode, setIsEditMode] = useState(!activeServiceOption);

  const handleChangeSuggestedService = useCallback((serviceId: string | OptionKey) => {
    handleSetFormValues({ serviceId: serviceId as string });
  }, [handleSetFormValues]);

  const handleChangeService = useCallback((serviceId?: string | OptionKey) => {
    handleSetFormValues({ serviceId: serviceId as string, customServiceId: serviceId as string });
    setIsEditMode(false);
  }, [handleSetFormValues]);

  return (
    <Box data-testid="service-setup">
      <FieldSetupLabel
        testId="service"
        hasValue={!!values?.serviceId}
        title={t('What was this expense for?')}
        isMobile={isMobile}
      />

      {values?.dateOfService ? null : (
        <SelectADateOfServiceFirstWarning margin={{ top: 'spacing24' }} />
      )}

      {suggestedServiceOptions?.length ? (
        <Box 
          data-testid="suggested-service-options"
          margin={{ top: 'spacing24' }}
        >
          <Text weight={700}>
            {suggestedServiceOptions?.length > 1 
              ? t('Services found on your document')
              : t('Service found on your document')}
          </Text>
          <FieldSetupOptions
            testId="suggested-service"
            margin={{ top: 'spacing8' }}
            value={values?.serviceId}
            onChangeValue={handleChangeSuggestedService}
            options={suggestedServiceOptions}
            style={{
              width: '100%',
              flexWrap: 'wrap',
            }}
          />
        </Box>
      ) : null}

      {/* Custom service setup */}
      <Box margin={{ top: 'spacing24' }}>
        <Text weight={700}>
          {t('Not right? Describe or select the expense')}
        </Text>
        <Box margin={{ top: 'spacing8' }} align="start">
          <EditableBadge
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            title={activeServiceOption?.title}
            value={values?.customServiceId as string}
            isSelected={values?.serviceId === values?.customServiceId}
            onChangeValue={handleChangeService}
          >
            <ServiceDescriptionField
              serviceOptions={serviceOptions}
              aiSuggestedAccountOrdersArrays={aiSuggestedAccountOrdersArrays}
              isAiSuggestedAccountOrdersLoading={isAiSuggestedAccountOrdersLoading}
              handleChangeService={handleChangeService}
              servicesGrouping={servicesGrouping}
              setServicesGrouping={setServicesGrouping}
              isDisabled={!values?.dateOfService}
            />
          </EditableBadge>
        </Box>
      </Box>

      {hasAvailableAccounts ? null : (
        <WarningMessage
          testId="have-no-plans"
          margin={{ top: 'spacing24' }}
          title={t(`The previously selected service is not covered by the date of service selected, ${dayjs(values?.dateOfService).format(DEFAULT_DATE_FORMAT)}. Please select a new service.`)}
        />
      )}
    </Box>
  );
};

export default ServiceDescriptionFieldSetup;
